<template>
  <b-container>
    <div class="b-main-content">
      <div class="d-flex align-items-center justify-content-between">
        <h2>Pacchetti</h2>

        <b-button v-show="!isSyncing" variant="outline-primary" @click="syncPackages()">Aggiorna</b-button>
        <b-button v-show="isSyncing" variant="outline-primary" disabled>
          <b-spinner style="width: 1.4rem; height: 1.4rem;" label="In caricamento..."></b-spinner>
          <span class="sr-only">In caricamento...</span>
        </b-button>
      </div>

      <div>
        <b-form @submit="onSubmit" class="d-flex" inline>
          <b-form-input
            v-model="filter"
            type="search"
            id="filterInput"
            class="mr-sm-2 flex-grow-1"
            placeholder="Cerca un pacchetto"
            debounce="400"
          ></b-form-input>

          <b-form-select v-model="selected" @change="filterByTag()" :options="tags">
            <template v-slot:first>
              <option :value="null">Filtra per tag</option>
            </template>
          </b-form-select>
        </b-form>
      </div>

      <b-table
        hover
        class="mt-3"
        show-empty
        :items="tableItems"
        :fields="tableFields"
        :sort-by.sync="sortBy"
        :sort-desc.sync="sortDesc"
        :busy.sync="isBusy"
        :filter="filter"
        :filterIncludedFields="filterOn"
      >
        <template v-slot:cell(name)="row">
          <b-link
            class="table-link"
            :to="{
              name: 'getPackageById',
              params: { id: row.item.package_id }
            }"
          >{{ row.item.name }}</b-link>
        </template>

        <template v-slot:cell(description)="row">
          <b-link
            class="table-link"
            :to="{
              name: 'getPackageById',
              params: { id: row.item.package_id }
            }"
          >{{ row.item.description }}</b-link>
        </template>

        <template v-slot:cell(created_at)="row">
          {{
          formatDate(row.item.created_at)
          }}
        </template>

        <template v-slot:cell(updated_at)="row">
          {{
          formatDate(row.item.updated_at)
          }}
        </template>

        <template v-slot:cell(metadata)="row">
          <b-badge
            class="mr-1"
            v-for="(tag, index) in row.item.metadata"
            :key="index"
            variant="secondary"
          >{{ tag }}</b-badge>
        </template>

        <template v-slot:table-busy>
          <div class="text-center text-secondary my-2">
            <b-spinner class="align-middle"></b-spinner>
          </div>
        </template>

        <template v-slot:empty>
          <div class="text-center">
            <img
              src="/datacatalog-simple/images/fogg-no-packages.png"
              alt="Nessun pacchetto trovato."
              class="empty-table"
            />
          </div>

          <h4 class="text-center text-empty mb-2">Non ci sono pacchetti disponibili</h4>
        </template>

        <template v-slot:emptyfiltered>
          <div class="text-center">
            <img
              src="/datacatalog-simple/images/fogg-no-packages.png"
              alt="Nessun pacchetto trovato."
              class="empty-table"
            />
          </div>

          <h4 class="text-center text-empty mb-2">Nessun pacchetto trovato</h4>
        </template>
      </b-table>
    </div>
  </b-container>
</template>

<script>
/*
  This page lists all packages visible to the user in a searchable, filterable and orderable table
*/

import { mapState } from 'vuex'
import store from '@/store'

export default {
  data () {
    return {
      tableFields: [
        { key: 'name', label: 'Nome', sortable: true },
        { key: 'description', label: 'Descrizione' },
        { key: 'created_at', label: 'Creato', sortable: true },
        { key: 'updated_at', label: 'Ultima modifica', sortable: true },
        { key: 'metadata', label: 'Tags' }
      ],
      sortBy: 'updated_at',
      sortDesc: true,
      isBusy: true,
      isSyncing: false,
      filter: null,
      filterOn: ['name', 'description', 'metadata'],
      selected: null,
      syncing: false
    }
  },
  async mounted () {
    // retrieve packages visibile to the user
    if (window.localStorage.getItem('cognito-vue-dashboard-default')) {
      const packages =
        JSON.parse(
          JSON.parse(window.localStorage.getItem('cognito-vue-dashboard-default'))
            .value
        ).packages.packages || null
      if (packages && store.state.packages.packages) {
        this.isBusy = false
        await store.dispatch('packages/retrievePackages')
        return
      }
    }

    await store.dispatch('packages/retrievePackages')
    this.isBusy = false
  },
  computed: {
    ...mapState({
      tableItems: state => {
        if (state.packages.packages) {
          // improve package objects for better readability and table processing
          return state.packages.packages.map(pkg => {
            const newPkg = Object.assign({}, pkg)
            return Object.assign(newPkg, {
              // modify tags and convert them to strings in order to render them more readable and useful in the table
              metadata: pkg.metadata.map(data => `${data.tag.trim()}: ${data.value.trim()}`),
              // truncate the description string to 250 characters and add an ellipsis
              description: pkg.description.substring(0, 250) + '...'
            })
          })
        }
        return state.packages.packages
      }
    }),
    // get unique tags from the packages in order to show them on the tag filtering dropdown
    tags () {
      if (this.tableItems) {
        const tags = []

        this.tableItems.map(item => {
          if (item.metadata.length) {
            item.metadata.map(data => tags.push(data))
          }
        })

        return [...new Set(tags)]
      }

      return []
    }
  },
  methods: {
    // format the package date to a more readable one
    formatDate: date =>
      date
        .split('T')
        .join(' ')
        .split('Z')[0],
    // refresh packages
    async syncPackages () {
      if (!this.isBusy) {
        this.isSyncing = true
        await store.dispatch('packages/retrievePackages')
        this.isSyncing = false
      }
    },
    // filter packages by tag (with the tag filtering dropdown)
    filterByTag () {
      this.filter = this.selected
    },
    onSubmit (evt) {
      evt.preventDefault()
    }
  }
}
</script>

<style scoped>
.table-link {
  color: var(--color-base);
  text-decoration: none;
}

.download-icon {
  width: 24px;
  height: 24px;
  color: var(--color-base);
}

.table-link:hover,
.download-icon:hover {
  color: var(--color-hover);
}

.table-link:active,
.download-icon:active {
  color: var(--color-active);
}

img.empty-table {
  max-width: 45%;
}

h4.text-empty {
  font-weight: 300;
}
</style>
